import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";

import { WithPopoverMixin, type WithPopoverProps } from "@/internals/mixins/with-popover-mixin";
import { filterElementsByTagNames } from "@/internals/elements";
import type { TextSize, FieldSize } from "@/internals/size";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";

import styles from "./atlas-labeled-content.scss";

import "@/components/display/atlas-icon-button/atlas-icon-button";
import "@/components/display/atlas-text/atlas-text";
import "@/components/layout/atlas-layout/atlas-layout";

export type LabeledContentProps = AtlasElementProps &
    WithPopoverProps & {
        label: string;
    };

/**
 * @slot - Conteúdo do componente
 *
 * @tag atlas-labeled-content
 */
@customElement("atlas-labeled-content")
export default class AtlasLabeledContent extends WithPopoverMixin(AtlasElement) {
    static styles = styles;

    /** Texto que aparece acima do conteúdo */
    @property({ type: String }) label: string;

    /** O tamanho do labeled content */
    @property({ type: String }) size: FieldSize = "md";

    private onSlotChange() {
        const slottedElements = this.shadowRoot?.querySelector("slot").assignedElements({ flatten: true }) || [];

        filterElementsByTagNames(slottedElements, ["ATLAS-LINK", "ATLAS-TEXT"]).forEach((element) => {
            if (element.hasAttribute("size")) return;

            element.setAttribute("size", this.size);
        });
    }

    private getLabelSize(): TextSize {
        const labelSizeForText = {
            "lg": "md",
            "md": "sm",
            "sm": "xs"
        };

        return (labelSizeForText[this.size] || "sm") as TextSize;
    }

    private renderLabel() {
        return when(
            !!this.label,
            () => html`
                <label for="${this.id}" class="form-label">
                    <atlas-text size="${this.getLabelSize()}">${this.label}</atlas-text>
                    ${this.renderPopoverInfoIcon()}
                </label>
            `
        );
    }

    /** @internal */
    public render() {
        const controlClass = {
            "labeled-content": true,
            [`${this.size}`]: !!this.size,
            "skeleton": this.skeletonLoading
        }

        return html`
            <atlas-layout gap="2" alignment="center" inline mobile-inline>
                ${this.renderLabel()}
            </atlas-layout>
            <div class="${classMap(controlClass)}">
                <slot @slotchange="${this.onSlotChange}"></slot>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-labeled-content": AtlasLabeledContent;
    }
}
